import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../useFocusTrap';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** Nom accessible quand il n'y a pas de titre visible. */
  ariaLabel?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnOverlayClick?: boolean;
  /** false pendant une action en cours (enregistrement) : Échap et clic extérieur ignorés. */
  dismissible?: boolean;
}

/**
 * Fenêtre modale : rendue dans <body> (jamais rognée ni décalée par l'écran), nommée par
 * son titre, focus piégé à l'intérieur et rendu au bouton d'ouverture, Échap pour fermer.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  ariaLabel,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  dismissible = true,
}) => {
  const titleId = useId();
  const dialogRef = useFocusTrap<HTMLDivElement>(isOpen, dismissible ? onClose : undefined);

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/40 transition-opacity"
          aria-hidden="true"
          onClick={closeOnOverlayClick && dismissible ? onClose : undefined}
        />

        {/* Modal */}
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-label={title ? undefined : ariaLabel}
          tabIndex={-1}
          className={`relative bg-white rounded-2xl shadow-xl border border-black/[0.08] w-full ${sizeClasses[size]} animate-fade-in-up focus:outline-none`}
        >
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h2 id={titleId} className="section-title text-lg">{title}</h2>
              <button
                type="button"
                onClick={onClose}
                disabled={!dismissible}
                aria-label="Fermer"
                className="w-9 h-9 -mr-2 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors disabled:opacity-40"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Content */}
          <div className={`px-6 ${title ? 'pb-6' : 'py-6'} text-gray-900`}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="px-6 py-4 border-t border-black/[0.06] bg-cream rounded-b-2xl">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
